import { transcribeOperationalAudio } from "@/core/services/operational-ai.service";
import { showToast } from "@/core/utils/ui";

type DictationState = "idle" | "starting" | "recording" | "transcribing";

export class OperationalDictationController {
  private mediaRecorder: MediaRecorder | null = null;
  private microphoneStream: MediaStream | null = null;
  private audioChunks: Blob[] = [];
  private state: DictationState = "idle";
  private recordingStartedAt = 0;
  private recordingTimer: number | null = null;
  private session = 0;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private streamSource: MediaStreamAudioSourceNode | null = null;
  private animationFrame: number | null = null;
  private frequencyData: Uint8Array<ArrayBuffer> | null = null;
  private smoothedLevels: number[] = [];
  private lastToggleAt = 0;

  constructor(
    private readonly container: HTMLElement,
    private readonly onStateChange: () => void,
  ) {}

  get busy(): boolean {
    return this.state !== "idle";
  }

  toggle(): void {
    const now = Date.now();
    if (now - this.lastToggleAt < 600) return;
    this.lastToggleAt = now;

    if (this.state === "recording") this.stop();
    else if (this.state === "idle") void this.start();
  }

  refreshUI(): void {
    const button = this.container.querySelector<HTMLButtonElement>("#opai-dictate-btn");
    const icon = this.container.querySelector<HTMLElement>("#opai-mic-icon");
    const input = this.container.querySelector<HTMLTextAreaElement>("#opai-message-input");
    const wrapper = this.container.querySelector<HTMLElement>(".opai-input-wrapper");
    const panel = this.container.querySelector<HTMLElement>("#opai-recording-panel");
    const timer = this.container.querySelector<HTMLElement>("#opai-recording-timer");
    const waveform = this.container.querySelector<HTMLCanvasElement>("#opai-live-waveform");
    const transcribingLabel = this.container.querySelector<HTMLElement>("#opai-transcribing-label");
    if (!button || !icon || !input || !panel || !timer || !waveform || !transcribingLabel) return;
    const recording = this.state === "recording";
    const transcribing = this.state === "transcribing";
    const starting = this.state === "starting";

    button.disabled = transcribing || starting;
    button.setAttribute("aria-pressed", String(recording));
    button.setAttribute("aria-label", recording ? "Detener dictado" : transcribing ? "Transcribiendo audio" : starting ? "Iniciando micrófono..." : "Iniciar dictado por voz");
    button.classList.toggle("is-recording", recording);
    button.classList.toggle("is-transcribing", transcribing);

    icon.className = transcribing || starting ? "ph ph-circle-notch animate-spin text-lg" : recording ? "ph-fill ph-stop text-sm" : "ph ph-microphone text-lg";

    const showInput = this.state === "idle" || this.state === "starting";
    input.classList.toggle("hidden", !showInput);
    panel.classList.toggle("hidden", showInput);
    panel.classList.toggle("flex", !showInput);

    timer.classList.toggle("hidden", transcribing);
    waveform.classList.toggle("hidden", !recording);
    transcribingLabel.classList.toggle("hidden", !transcribing);
    transcribingLabel.classList.toggle("flex", transcribing);
    if (!showInput) wrapper?.classList.remove("is-expanded");
    else if (input.value) {
      window.requestAnimationFrame(() => input.dispatchEvent(new Event("input")));
    }
    if (recording) {
      const seconds = Math.floor((Date.now() - this.recordingStartedAt) / 1000);
      timer.textContent = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
    }
    this.onStateChange();
  }

  cancel(): void {
    this.session += 1;
    this.clearTimer();
    if (this.mediaRecorder?.state !== "inactive") this.mediaRecorder?.stop();
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.state = "idle";
    this.stopVisualizer();
    this.releaseMicrophone();
    this.refreshUI();
  }

  private async start(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      showToast("Este navegador no permite grabar audio. Actualízalo o usa Chrome, Edge, Safari o Firefox.", "warning");
      return;
    }
    try {
      const currentSession = ++this.session;
      this.state = "starting";
      this.refreshUI();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      if (currentSession !== this.session) { stream.getTracks().forEach((track) => track.stop()); return; }
      const mimeType = this.preferredMimeType();
      this.microphoneStream = stream;
      this.audioChunks = [];
      this.mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64_000 }) : new MediaRecorder(stream);
      this.mediaRecorder.addEventListener("dataavailable", (event) => { if (event.data.size > 0) this.audioChunks.push(event.data); });
      this.mediaRecorder.addEventListener("stop", () => void this.finish(currentSession), { once: true });
      this.mediaRecorder.addEventListener("error", () => {
        showToast("El navegador interrumpió la grabación. Intenta nuevamente.", "error");
        this.cancel();
      }, { once: true });
      this.state = "recording";
      this.recordingStartedAt = Date.now();
      this.mediaRecorder.start(250);
      this.refreshUI();
      this.startVisualizer(stream);
      this.recordingTimer = window.setInterval(() => {
        this.refreshUI();
        if (Date.now() - this.recordingStartedAt >= 90_000) this.stop();
      }, 500);
    } catch (error) {
      const denied = error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError");
      showToast(denied ? "El micrófono está bloqueado. Permítelo en el navegador y vuelve a intentarlo." : "No se pudo abrir el micrófono. Verifica que no esté ocupado por otra aplicación.", "error");
      this.cancel();
    }
  }

  private stop(): void {
    if (this.state !== "recording" || !this.mediaRecorder) return;
    this.state = "transcribing";
    this.clearTimer();
    this.refreshUI();
    this.stopVisualizer();
    if (this.mediaRecorder.state !== "inactive") this.mediaRecorder.stop();
    this.releaseMicrophone();
  }

  private async finish(currentSession: number): Promise<void> {
    if (currentSession !== this.session) return;

    const durationMs = Date.now() - this.recordingStartedAt;
    const mimeType = this.mediaRecorder?.mimeType || this.audioChunks[0]?.type || "audio/webm";
    const audio = new Blob(this.audioChunks, { type: mimeType });
    this.audioChunks = [];
    this.mediaRecorder = null;
    let transcribedInput: HTMLTextAreaElement | null = null;
    try {
      if (durationMs < 500) throw new Error("El dictado fue demasiado corto.");
      const transcript = await transcribeOperationalAudio(audio, durationMs);
      if (currentSession !== this.session) return;
      const input = this.container.querySelector<HTMLTextAreaElement>("#opai-message-input");
      if (input) {
        const currentText = input.value.trim();
        input.value = currentText ? `${currentText} ${transcript}` : transcript;
        transcribedInput = input;
      }
      showToast("Dictado convertido a texto", "success");
    } catch (error) {
      if (currentSession === this.session) showToast(error instanceof Error ? error.message : "No se pudo transcribir el dictado.", "error");
    } finally {
      if (currentSession === this.session) {
        this.state = "idle";
        this.stopVisualizer();
        this.refreshUI();
        if (transcribedInput) {
          const input = transcribedInput;
          window.requestAnimationFrame(() => {
            if (currentSession !== this.session) return;
            input.dispatchEvent(new Event("input"));
            input.focus();
          });
        }
      }
    }
  }

  private preferredMimeType(): string {
    return ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4", "audio/webm"]
      .find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? "";
  }

  private clearTimer(): void {
    if (this.recordingTimer !== null) window.clearInterval(this.recordingTimer);
    this.recordingTimer = null;
  }

  private startVisualizer(stream: MediaStream): void {
    this.stopVisualizer();
    this.audioContext = new AudioContext();
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.72;
    this.analyser.minDecibels = -75;
    this.analyser.maxDecibels = -15;
    this.streamSource = this.audioContext.createMediaStreamSource(stream);
    this.streamSource.connect(this.analyser);
    this.frequencyData = new Uint8Array(new ArrayBuffer(this.analyser.frequencyBinCount));
    this.smoothedLevels = Array.from({ length: 42 }, () => 0);
    this.drawWaveform();
  }

  private drawWaveform(): void {
    if (this.state !== "recording" || !this.analyser || !this.frequencyData) return;
    const canvas = this.container.querySelector<HTMLCanvasElement>("#opai-live-waveform");
    if (!canvas) return;

    const width = Math.max(1, canvas.clientWidth);
    const height = Math.max(1, canvas.clientHeight);
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const targetWidth = Math.floor(width * pixelRatio);
    const targetHeight = Math.floor(height * pixelRatio);
    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
    }

    const context = canvas.getContext("2d");
    if (context) {
      this.analyser.getByteFrequencyData(this.frequencyData);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      context.clearRect(0, 0, width, height);
      context.strokeStyle = "rgba(255, 255, 255, 0.95)";
      context.lineCap = "round";

      const bars = this.smoothedLevels.length;
      const gap = 2.6;
      const barWidth = Math.max(1.5, (width - gap * (bars - 1)) / bars);
      const centerIndex = (bars - 1) / 2;
      context.lineWidth = barWidth;

      for (let index = 0; index < bars; index += 1) {
        const relativeDistance = Math.abs(index - centerIndex) / centerIndex;
        const bin = 2 + Math.floor(relativeDistance * Math.min(32, this.frequencyData.length - 10));
        const level = Math.max(0, (this.frequencyData[bin] - 18) / 237);
        this.smoothedLevels[index] = this.smoothedLevels[index] * 0.62 + level * 0.38;
        const domeFactor = Math.cos(relativeDistance * Math.PI / 2);
        const barHeight = Math.max(2.5, Math.pow(this.smoothedLevels[index], 0.78) * (height - 4) * (0.35 + 0.65 * domeFactor));
        const x = index * (barWidth + gap) + barWidth / 2;
        context.beginPath();
        context.moveTo(x, (height - barHeight) / 2);
        context.lineTo(x, (height + barHeight) / 2);
        context.stroke();
      }
    }

    this.animationFrame = window.requestAnimationFrame(() => this.drawWaveform());
  }

  private stopVisualizer(): void {
    if (this.animationFrame !== null) window.cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
    this.streamSource?.disconnect();
    this.streamSource = null;
    this.analyser?.disconnect();
    this.analyser = null;
    this.frequencyData = null;
    if (this.audioContext) void this.audioContext.close();
    this.audioContext = null;
    this.smoothedLevels = [];
  }

  private releaseMicrophone(): void {
    this.microphoneStream?.getTracks().forEach((track) => track.stop());
    this.microphoneStream = null;
  }
}
